<template>
	<div class="flex flex-col gap-1">
		<p class="flex">
			<span
				:class="[
					size === 'medium' ? 'text-xl' : 'text-sm',
					isDiscountedProduct() ? 'text-redcustom' : 'text-price',
					weightClass,
					'price-current'
				]"
			>
				{{ formattedPrice }}
			</span>
			<span
				v-if="isDiscountedProduct()"
				:class="[
					'pl-2 line-through text-gray-super-dark',
					size === 'medium' ? 'text-lg' : 'text-sm font-normal',
					'price-old'
				]"
			>
				{{ formattedOldPrice }}
			</span>
		</p>
		<p
			v-if="oldPrice && isDiscountedProduct() && showSaveText"
			class="text-xs text-redcustom"
		>
			{{ $t("Save") }} {{ formatPrice(oldPrice - price) }} ({{ percent }}%)
		</p>
		<p
			v-if="oldPrice && showPrevious"
			class="text-xs text-gray-super-dark text-left"
		>
			{{ $t("Previously") }} {{ formattedOldPrice }}
		</p>
		<div
			v-if="isProductPage && historicalLowestPrice && !lowestPriceAlways"
			class="text-xs text-left pt-2"
		>
			<Popover class="relative">
				<PopoverButton
					id="history-popover-button"
					class="flex items-center gap-1 outline-none text-gray-super-dark"
				>
					<InformationCircleIcon class="w-5 h-5 min-w-5" />
					{{ $t("View price history") }}
				</PopoverButton>
				<PopoverPanel
					id="history-popover-panel"
					class="absolute top-6 left-0 z-10 py-1 px-2 min-w-56 max-h-[480px] bg-white border border-gray-600"
				>
					{{
						$t("Lowest price in the last 30 days: {value}", {
							value: formatPrice(historicalLowestPrice)
						})
					}}
				</PopoverPanel>
			</Popover>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { formatPriceWithSymbol } from "@/utils/currencyConfig";
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";

const FONT_WEIGHT_CLASS = {
	normal: "font-normal",
	medium: "font-medium",
	semibold: "font-semibold",
	bold: "font-bold"
} as const;

type FontWeight = keyof typeof FONT_WEIGHT_CLASS;

interface Props {
	price: number;
	oldPrice?: number | null;
	size?: string;
	showSaveText?: boolean;
	showPrevious?: boolean;
	isProductPage?: boolean;
	/** Overrides the weight this size/discount would pick on its own. */
	fontWeight?: FontWeight;
}

const {
	size,
	oldPrice,
	price,
	showSaveText,
	showPrevious = true,
	fontWeight
} = defineProps<Props>();

const norceStore = useNorceStore();
const { lowestPrice: historicalLowestPrice } = storeToRefs(useProductStore());
// With the note on every product it shows in the delivery rows instead.
const { lowestPriceAlways } = useProductDetailsConfig();

const formatPrice = (value: number) =>
	formatPriceWithSymbol(value, norceStore?.currentCurrency?.Code);

const formattedPrice = computed(() => formatPrice(price));
const formattedOldPrice = computed(() =>
	oldPrice ? formatPrice(oldPrice) : ""
);

const percent = computed(() => {
	if (oldPrice && price) {
		const discount = Math.floor(((oldPrice - price) / oldPrice) * 100);

		if (discount) {
			return `-${discount}`;
		}
		return 0;
	}
	return null;
});

const isDiscountedProduct = () => oldPrice && oldPrice > price;

/**
 * Exactly one weight class reaches the element. Stacking several would leave
 * the winner to Tailwind's generated order rather than to this list.
 */
const weightClass = computed(() => {
	if (fontWeight) return FONT_WEIGHT_CLASS[fontWeight];
	// Order matters and mirrors what the stacked classes used to resolve to:
	// a medium price stayed semibold even when discounted.
	if (size === "medium") return "font-semibold";
	return isDiscountedProduct() ? "font-medium" : "font-normal";
});
</script>
